import { useCallback, useEffect, useRef, useState } from "react";
import { RotateCcw, RotateCw, Trash2, Upload, X } from "lucide-react";
import { getProfile } from "../../../api/authProfessional";
import { updateProfessionnel, uploadSignatureFile, updateSignatureUrl } from "../../../api/professional";
import { professionalFromUser, useAuthProfessionalStore } from "../../../store/authProfessionalStore";
import { RequiredMark } from "../../../components/RequiredMark";
import MesEtablissements from "./MesEtablissements";
import type { ProfessionalUser } from "../../../api/authProfessional";

function extractSignatureUrl(u: ProfessionalUser): string | null {
  const candidates = [
    u.signatureUrlLink,
    u.signature_url_link,
    (u.profile as Record<string, unknown> | undefined)?.signatureUrlLink,
    (u.profile as Record<string, unknown> | undefined)?.signature_url_link,
    (u.informationsPersonnelles as Record<string, unknown> | undefined)?.signatureUrlLink,
    (u.informationsPersonnelles as Record<string, unknown> | undefined)?.signature_url_link,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return null;
}

// ─── SignatureBox ──────────────────────────────────────────────────────────────

type SignatureBoxProps = {
  nip: string;
  currentUrl: string | null;
  onSaved: (url: string | null) => void;
};

function SignatureBox({ nip, currentUrl, onSaved }: SignatureBoxProps) {
  const [tab, setTab] = useState<"import" | "draw">("import");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const drewRef = useRef(false); // vrai si au moins un trait a été tracé depuis startDraw
  const [canvasEmpty, setCanvasEmpty] = useState(true);

  // ── Historique undo/redo ─────────────────────────────────────────────────
  const snapshotsRef = useRef<ImageData[]>([]);
  const snapIndexRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // ── Tracés vectoriels (parallèle aux snapshots ImageData) — permet d'exporter
  // la signature dessinée en SVG plutôt qu'en PNG rasterisé.
  const strokesHistoryRef = useRef<{ x: number; y: number }[][][]>([[]]);
  const currentStrokeRef = useRef<{ x: number; y: number }[]>([]);

  function syncUndoRedo() {
    setCanUndo(snapIndexRef.current > 0);
    setCanRedo(snapIndexRef.current < snapshotsRef.current.length - 1);
  }

  function saveSnapshot(strokes: { x: number; y: number }[][]) {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const snap = ctx.getImageData(0, 0, canvas.width, canvas.height);
    // Supprime les états "futurs" (redo)
    snapshotsRef.current = snapshotsRef.current.slice(0, snapIndexRef.current + 1);
    snapshotsRef.current.push(snap);
    strokesHistoryRef.current = strokesHistoryRef.current.slice(0, snapIndexRef.current + 1);
    strokesHistoryRef.current.push(strokes);
    snapIndexRef.current = snapshotsRef.current.length - 1;
    syncUndoRedo();
  }

  function undo() {
    if (snapIndexRef.current <= 0) return;
    snapIndexRef.current--;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.putImageData(snapshotsRef.current[snapIndexRef.current], 0, 0);
    setCanvasEmpty(snapIndexRef.current === 0);
    syncUndoRedo();
  }

  function redo() {
    if (snapIndexRef.current >= snapshotsRef.current.length - 1) return;
    snapIndexRef.current++;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.putImageData(snapshotsRef.current[snapIndexRef.current], 0, 0);
    setCanvasEmpty(false);
    syncUndoRedo();
  }

  // Ref stables pour les raccourcis clavier (évite les stale closures)
  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  undoRef.current = undo;
  redoRef.current = redo;

  // ── Init canvas ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (tab !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "#0B1F16";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // État initial dans l'historique
    const initial = ctx.getImageData(0, 0, canvas.width, canvas.height);
    snapshotsRef.current = [initial];
    strokesHistoryRef.current = [[]];
    snapIndexRef.current = 0;
    setCanUndo(false);
    setCanRedo(false);
    setCanvasEmpty(true);
  }, [tab]);

  // ── Raccourcis Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z ───────────────────────────
  useEffect(() => {
    if (tab !== "draw") return;
    function onKey(e: KeyboardEvent) {
      if (!e.ctrlKey && !e.metaKey) return;
      if (e.key === "z" && !e.shiftKey) { e.preventDefault(); undoRef.current(); }
      if (e.key === "y" || (e.key === "z" && e.shiftKey)) { e.preventDefault(); redoRef.current(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tab]);

  // ── Dessin — logique partagée (accède uniquement aux refs) ─────────────────
  function clientPosOnCanvas(clientX: number, clientY: number) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const r = canvas.getBoundingClientRect();
    return { x: clientX - r.left, y: clientY - r.top };
  }

  function beginStroke(pos: { x: number; y: number }) {
    drawingRef.current = true;
    drewRef.current = false;
    currentStrokeRef.current = [pos];
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }

  function continueStroke(pos: { x: number; y: number }) {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    currentStrokeRef.current.push(pos);
    drewRef.current = true;
  }

  function endStroke() {
    if (drawingRef.current && drewRef.current) {
      setCanvasEmpty(false);
      const strokes = [...(strokesHistoryRef.current[snapIndexRef.current] ?? []), currentStrokeRef.current];
      saveSnapshot(strokes);
    }
    drawingRef.current = false;
    drewRef.current = false;
    currentStrokeRef.current = [];
  }

  // ── Handlers souris (React synthetic, pas de problème passive) ───────────
  function onMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    const pos = clientPosOnCanvas(e.clientX, e.clientY);
    if (pos) beginStroke(pos);
  }
  function onMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const pos = clientPosOnCanvas(e.clientX, e.clientY);
    if (pos) continueStroke(pos);
  }

  // ── Listeners touch non-passifs (DOM natif) ──────────────────────────────
  useEffect(() => {
    if (tab !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    function onTouchStart(e: TouchEvent) {
      e.preventDefault();
      const t = e.touches[0];
      if (!t) return;
      const pos = clientPosOnCanvas(t.clientX, t.clientY);
      if (pos) beginStroke(pos);
    }
    function onTouchMove(e: TouchEvent) {
      e.preventDefault();
      const t = e.touches[0];
      if (!t) return;
      const pos = clientPosOnCanvas(t.clientX, t.clientY);
      if (pos) continueStroke(pos);
    }
    function onTouchEnd(e: TouchEvent) {
      e.preventDefault();
      endStroke();
    }

    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove",  onTouchMove,  { passive: false });
    canvas.addEventListener("touchend",   onTouchEnd,   { passive: false });
    return () => {
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove",  onTouchMove);
      canvas.removeEventListener("touchend",   onTouchEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  function clearCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
    setCanvasEmpty(true);
    saveSnapshot([]);
  }

  /** Exporte les traits accumulés en SVG vectoriel (pas de rasterisation canvas). */
  function canvasToSvgFile(): File | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;
    const strokes = (strokesHistoryRef.current[snapIndexRef.current] ?? []).filter((s) => s.length > 0);
    if (strokes.length === 0) return null;
    const paths = strokes
      .map((stroke) => {
        const d = stroke.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
        return `<path d="${d}" stroke="#0B1F16" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`;
      })
      .join("");
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">${paths}</svg>`;
    return new File([svg], `sig_${nip}.svg`, { type: "image/svg+xml" });
  }

  async function handleSave() {
    setError(null);
    setSuccess(false);
    let toUpload: File | null = null;
    if (tab === "import") {
      if (!file) { setError("Sélectionnez d'abord une image."); return; }
      toUpload = file;
    } else {
      if (canvasEmpty) { setError("Dessinez d'abord votre signature."); return; }
      toUpload = canvasToSvgFile();
      if (!toUpload) { setError("Impossible de lire le dessin."); return; }
    }
    setUploading(true);
    const up = await uploadSignatureFile(toUpload, nip);
    if (!up.ok) { setUploading(false); setError(up.message); return; }
    const save = await updateSignatureUrl(nip, up.url);
    setUploading(false);
    if (!save.ok) { setError(save.message); return; }
    setSuccess(true);
    onSaved(up.url);
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setCanvasEmpty(true);
  }

  async function handleDelete() {
    setError(null);
    setDeleting(true);
    const res = await updateSignatureUrl(nip, null);
    setDeleting(false);
    if (!res.ok) {
      if (res.status === 404) {
        setError(
          "La route de suppression de signature (PATCH /professionnel/:nip/signature) n'existe pas encore côté backend. " +
          "Merci de la créer — voir la demande dans docs/frontend/professionnel/ROUTE_SIGNATURE_A_IMPLEMENTER.md"
        );
      } else {
        setError(res.message);
      }
      return;
    }
    onSaved(null);
  }

  const canSave = tab === "import" ? !!file : !canvasEmpty;

  return (
    <div className="mt-6 rounded-[12px] border bg-white p-6" style={{ borderColor: "#E5E7EB" }}>
      <h2 className="mb-4 text-base font-semibold text-[#0B1F16]">Signature</h2>

      {/* Signature actuelle */}
      {!currentUrl && (
        <div className="mb-5 flex items-center gap-2 rounded-[10px] border border-dashed px-4 py-3 text-sm text-gray-400" style={{ borderColor: "#D1D5DB" }}>
          <span className="italic">Pas encore de signature enregistrée.</span>
        </div>
      )}
      {currentUrl && (
        <div className="mb-5">
          <p className="mb-2 text-xs text-gray-500">Signature enregistrée</p>
          <div className="relative inline-flex items-center justify-center rounded-[10px] border bg-gray-50 px-4 py-3" style={{ borderColor: "#E5E7EB" }}>
            <img src={currentUrl} alt="Signature actuelle" className="h-16 max-w-[280px] object-contain" />
            <button
              type="button"
              title="Supprimer la signature"
              onClick={() => void handleDelete()}
              disabled={deleting}
              className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
            >
              <X size={10} />
            </button>
          </div>
        </div>
      )}

      {/* Onglets */}
      <div className="mb-4 flex gap-2">
        {(["import", "draw"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => { setTab(t); setError(null); setSuccess(false); }}
            className="h-8 rounded-[8px] px-4 text-sm font-medium transition-colors"
            style={{ background: tab === t ? "#00553E" : "#E5E7EB", color: tab === t ? "#fff" : "#0B1F16" }}
          >
            {t === "import" ? "Importer une image" : "Dessiner"}
          </button>
        ))}
      </div>

      {/* ── Import ── */}
      {tab === "import" && (
        <div>
          <label
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed py-8 transition hover:bg-gray-50"
            style={{ borderColor: preview ? "#00553E" : "#D1D5DB" }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setFile(f);
                setError(null);
                setSuccess(false);
                const reader = new FileReader();
                reader.onload = (ev) => setPreview(ev.target?.result as string);
                reader.readAsDataURL(f);
              }}
            />
            {preview ? (
              <img src={preview} alt="Aperçu signature" className="h-24 max-w-xs object-contain" />
            ) : (
              <>
                <Upload size={28} className="text-gray-400" />
                <span className="text-sm text-gray-500">PNG, JPG ou SVG · max 50 Mo</span>
                <span className="text-xs text-gray-400">Cliquer pour parcourir</span>
              </>
            )}
          </label>
          {preview && (
            <button
              type="button"
              onClick={() => { setFile(null); setPreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              className="mt-1.5 text-xs text-red-500 hover:text-red-700"
            >
              Retirer l'image
            </button>
          )}
        </div>
      )}

      {/* ── Canvas ── */}
      {tab === "draw" && (
        <div>
          <p className="mb-2 text-xs text-gray-500">
            Dessinez votre signature ci-dessous
            <span className="ml-2 text-gray-400">(Ctrl+Z / Ctrl+Y)</span>
          </p>
          <div className="overflow-hidden rounded-[10px] border-2" style={{ borderColor: "#D1D5DB" }}>
            <canvas
              ref={canvasRef}
              className="block w-full touch-none"
              style={{ height: "180px", cursor: "crosshair", background: "#fff" }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={endStroke}
              onMouseLeave={endStroke}
            />
          </div>

          {/* Barre undo / redo / effacer */}
          <div className="mt-2 flex items-center gap-1">
            <button
              type="button"
              title="Annuler (Ctrl+Z)"
              onClick={undo}
              disabled={!canUndo}
              className="flex h-7 w-7 items-center justify-center rounded-[6px] border text-[#0B1F16] transition hover:bg-gray-100 disabled:opacity-30"
              style={{ borderColor: "#E5E7EB" }}
            >
              <RotateCcw size={13} />
            </button>
            <button
              type="button"
              title="Rétablir (Ctrl+Y)"
              onClick={redo}
              disabled={!canRedo}
              className="flex h-7 w-7 items-center justify-center rounded-[6px] border text-[#0B1F16] transition hover:bg-gray-100 disabled:opacity-30"
              style={{ borderColor: "#E5E7EB" }}
            >
              <RotateCw size={13} />
            </button>
            <div className="mx-1.5 h-4 w-px bg-gray-200" />
            <button
              type="button"
              title="Tout effacer"
              onClick={clearCanvas}
              disabled={canvasEmpty}
              className="flex h-7 items-center gap-1 rounded-[6px] border px-2 text-xs text-red-500 transition hover:bg-red-50 disabled:opacity-30"
              style={{ borderColor: "#E5E7EB" }}
            >
              <Trash2 size={12} /> Effacer
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-3 rounded-[8px] border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          Signature enregistrée avec succès.
        </div>
      )}

      <button
        type="button"
        onClick={() => void handleSave()}
        disabled={!canSave || uploading}
        className="mt-4 h-10 rounded-[10px] px-5 text-sm font-semibold text-white disabled:opacity-50"
        style={{ background: "#00553E" }}
      >
        {uploading ? "Enregistrement…" : "Enregistrer la signature"}
      </button>
    </div>
  );
}

// ─── ProProfile ───────────────────────────────────────────────────────────────

const PROFILE_TABS = [
  { key: "infos", label: "Informations générales" },
  { key: "signature", label: "Signature" },
  { key: "etablissements", label: "Établissements" },
] as const;

type ProfileTab = (typeof PROFILE_TABS)[number]["key"];

export default function ProProfile() {
  const setProfessional = useAuthProfessionalStore((s) => s.setProfessional);

  const [tab, setTab] = useState<ProfileTab>("infos");
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [nip, setNip] = useState<string | undefined>(undefined);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [initial, setInitial] = useState({ nom: "", prenom: "", email: "" });
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const res = await getProfile({ silent401: false });
    if (!res.ok) {
      setLoadError(res.message);
      setLoading(false);
      return;
    }
    const u = res.data;
    const nextNom = typeof u.nom === "string" ? u.nom : "";
    const nextPrenom = typeof u.prenom === "string" ? u.prenom : "";
    const nextEmail = typeof u.email === "string" ? u.email : "";
    setNom(nextNom);
    setPrenom(nextPrenom);
    setEmail(nextEmail);
    setInitial({ nom: nextNom, prenom: nextPrenom, email: nextEmail });
    setNip(typeof u.nip === "string" ? u.nip : undefined);
    setSignatureUrl(extractSignatureUrl(u));
    setProfessional(professionalFromUser(u));
    setLoading(false);
  }, [setProfessional]);

  useEffect(() => { void load(); }, [load]);

  const dirty = nom !== initial.nom || prenom !== initial.prenom || email !== initial.email;

  const save = async () => {
    if (!nip) { setSaveError("NIP manquant — impossible de mettre à jour le profil côté serveur."); return; }
    setSaving(true);
    setSaveError(null);
    const res = await updateProfessionnel(nip, {
      nom: nom.trim() || undefined,
      prenom: prenom.trim() || undefined,
      email: email.trim() || undefined,
    });
    setSaving(false);
    if (!res.ok) { setSaveError(res.message); return; }
    await load();
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="w-full max-w-2xl">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0B1F16]">Profil</h1>
          <p className="mt-4 text-[#0B1F16]/70">Chargement…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="w-full max-w-2xl">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0B1F16]">Profil</h1>

          {loadError && (
            <div className="mt-4 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              {loadError}
            </div>
          )}
          {saveError && (
            <div className="mt-4 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              {saveError}
            </div>
          )}

          {/* ── Onglets ── */}
          <div className="mt-6 flex flex-wrap gap-2">
            {PROFILE_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className="h-10 rounded-[10px] px-4 text-sm font-medium transition-colors"
                style={{ background: tab === t.key ? "#00553E" : "#E5E7EB", color: tab === t.key ? "#fff" : "#0B1F16" }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Informations personnelles ── */}
          {tab === "infos" && (
          <div className="mt-6 bg-white rounded-[12px] border p-6" style={{ borderColor: "#E5E7EB" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="font-semibold mb-1">Nom</div>
                <input
                  disabled={!editing}
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3 disabled:opacity-80"
                  style={{ borderColor: "#E5E7EB" }}
                />
              </div>
              <div>
                <div className="font-semibold mb-1">Prénom <RequiredMark /></div>
                <input
                  disabled={!editing}
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3 disabled:opacity-80"
                  style={{ borderColor: "#E5E7EB" }}
                />
              </div>
              <div className="md:col-span-2">
                <div className="font-semibold mb-1">Email <RequiredMark /></div>
                <input
                  disabled={!editing}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10 w-full rounded-[10px] border bg-[#F3F4F4] px-3 disabled:opacity-80"
                  style={{ borderColor: "#E5E7EB" }}
                />
              </div>
              {nip && (
                <div className="md:col-span-2 text-sm text-[#0B1F16]/60">
                  NIP : <span className="font-mono text-[#0B1F16]">{nip}</span>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center gap-3">
              {!editing ? (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="h-10 px-4 rounded-[10px] font-semibold text-white"
                  style={{ background: "#00553E" }}
                >
                  Modifier
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setConfirmOpen(true)}
                    disabled={!dirty || saving}
                    className="h-10 px-4 rounded-[10px] font-semibold text-white disabled:opacity-60"
                    style={{ background: "#00553E" }}
                  >
                    {saving ? "Enregistrement…" : "Enregistrer"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditing(false); void load(); }}
                    className="h-10 px-4 rounded-[10px] font-semibold border"
                    style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
                  >
                    Annuler
                  </button>
                </>
              )}
            </div>
          </div>
          )}

          {/* ── Signature ── */}
          {tab === "signature" && nip && (
            <SignatureBox
              nip={nip}
              currentUrl={signatureUrl}
              onSaved={setSignatureUrl}
            />
          )}

          {/* ── Mes établissements ── */}
          {tab === "etablissements" && nip && <MesEtablissements nip={nip} />}
        </div>
      </div>

      {/* Modal confirmation infos */}
      {confirmOpen && (
        <div className="fixed inset-0 z-20">
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-[12px] p-6 w-full max-w-md shadow">
              <div className="text-lg font-semibold text-[#0B1F16]">Souhaitez-vous enregistrer les modifications ?</div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold text-white"
                  style={{ background: "#00553E" }}
                  onClick={() => { void save(); setConfirmOpen(false); }}
                >
                  Conserver
                </button>
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold border"
                  style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
                  onClick={() => setConfirmOpen(false)}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
